import { useMemo, useState } from 'react'
import type { CaseEndpoint } from '../../types/case-container'

interface UseEndpointSetModalArgs {
  endpointIds: Set<string>
  endpoints: CaseEndpoint[]
  onRemoveMany: (ids: string[]) => void
  onClear: () => void
}

interface UseEndpointSetModalResult {
  searchTerm: string
  setSearchTerm: (s: string) => void
  selectedIds: Set<string>
  showClearConfirm: boolean
  setEndpoints: CaseEndpoint[]
  filteredEndpoints: CaseEndpoint[]
  toggleSelect: (id: string) => void
  selectAll: () => void
  deselectAll: () => void
  handleRemoveSelected: () => void
  handleConfirmClear: () => void
  onShowClearConfirm: () => void
  onCancelClear: () => void
}

function matchesSearch(endpoint: CaseEndpoint, search: string): boolean {
  return (
    endpoint.hostname.toLowerCase().includes(search) ||
    endpoint.name.toLowerCase().includes(search) ||
    (endpoint.ipAddresses?.some((ip) => ip.includes(search)) ?? false) ||
    endpoint.deviceType.toLowerCase().includes(search)
  )
}

export function useEndpointSetModal({
  endpointIds,
  endpoints,
  onRemoveMany,
  onClear,
}: UseEndpointSetModalArgs): UseEndpointSetModalResult {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [showClearConfirm, setShowClearConfirm] = useState(false)

  const setEndpoints = useMemo(
    () => endpoints.filter((ep) => endpointIds.has(ep.id)),
    [endpoints, endpointIds]
  )

  const filteredEndpoints = useMemo(() => {
    if (!searchTerm) return setEndpoints
    const search = searchTerm.toLowerCase()
    return setEndpoints.filter((ep) => matchesSearch(ep, search))
  }, [setEndpoints, searchTerm])

  const toggleSelect = (id: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = (): void => {
    setSelectedIds(new Set(filteredEndpoints.map((ep) => ep.id)))
  }

  const deselectAll = (): void => {
    setSelectedIds(new Set())
  }

  const handleRemoveSelected = (): void => {
    onRemoveMany(Array.from(selectedIds))
    setSelectedIds(new Set())
  }

  const handleConfirmClear = (): void => {
    onClear()
    setShowClearConfirm(false)
  }

  return {
    searchTerm,
    setSearchTerm,
    selectedIds,
    showClearConfirm,
    setEndpoints,
    filteredEndpoints,
    toggleSelect,
    selectAll,
    deselectAll,
    handleRemoveSelected,
    handleConfirmClear,
    onShowClearConfirm: () => { setShowClearConfirm(true) },
    onCancelClear: () => { setShowClearConfirm(false) },
  }
}
